import { Injectable, OnDestroy } from '@angular/core';
import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/firestore';
import { AngularFireDatabase } from '@angular/fire/database';
import { AngularFireAuth } from '@angular/fire/auth';
import { from, Observable, of, Subject } from 'rxjs';
import { map, switchMap, shareReplay, takeUntil } from 'rxjs/operators';
import * as firebase from 'firebase/app';
import 'firebase/auth';
import { DebugLevel, DebugService } from '../debug/debug.service';

/**
 * Firebase utilities service
 * Some help: https://github.com/angular/angularfire/blob/master/docs/rtdb/lists.md
 */
@Injectable({ providedIn: 'root' })
export class FirebaseService implements OnDestroy {
  /** Guarda la referencia a la base de datos principal */
  private pageContentReference: AngularFirestoreCollection<any>;
  /** Stream a la base de datos principal de donde obtenemos el contenido de nuestra página */
  private pageContent$: Observable<any>;
  /** Stream destroyer */
  private unsubscriber$ = new Subject();

  constructor(private readonly firestore: AngularFirestore, private fireAuth: AngularFireAuth, private debugService: DebugService, private angularFireDatabase: AngularFireDatabase) { }

  public ngOnDestroy(): void {
    this.unsubscriber$.next();
    this.unsubscriber$.complete();
  }
  /**
   * Devuelve una referencia de tipo firestore collection a partir de un nombre
   * @param name
   */
  public getCollectionReference(name: string): AngularFirestoreCollection<any> {
    return this.firestore.collection(name);
  }
  /**
   * Establece el stream a la base de datos que proporciona el contenido principal de la aplicación
   * @param name
   */
  public setPageContentDatabase(name: string): void {
    this.debugService.debug(DebugLevel.TRACE, 'FirebaseService[ setPageContentDatabase ] : ', 'init');
    this.pageContentReference = this.firestore.collection(name);
    this.pageContent$ = this.pageContentReference.snapshotChanges().pipe(
      takeUntil(this.unsubscriber$),
      shareReplay(1),
      map((items) => {
        this.debugService.debug(DebugLevel.DEBUG, 'Firebase Service/snapshot-changes items: ', items);
        return items.map(item => {
          const data = item.payload.doc.data();
          const id = item.payload.doc.id;
          return { id, ...data };
        })
      })
    );
    this.debugService.debug(DebugLevel.TRACE, 'FirebaseService[ setPageContentDatabase ] : ', 'end');
  }
  /** Devuelve un observable al stream de cambios de la base de datos */
  public getPageContentAsObservable(): Observable<unknown[]> {
    return this.pageContent$;
  }
  /**
   * Guarda en la base de datos principal de la aplicación un nuevo item
   * @param item Objecto a guardar en la base de datos
   */
  public async addItem(item): Promise<void> {
    this.debugService.debug(DebugLevel.TRACE, 'FirebaseService[ addItem ]: ', 'init');
    const id = this.firestore.createId();
    const newItem = { id, ...item };
    try {
      await this.pageContentReference.doc(id).set(newItem);
      this.debugService.debug(DebugLevel.TRACE, 'FirebaseService[ addItem ]: ', 'end');
    } catch (error) {
      this.debugService.debug(DebugLevel.DEBUG, 'Error creating firebase item. ', error);
      this.debugService.debug(DebugLevel.ERROR, 'Error creating firebase item. ', null);
    }
  }
  /**
   * Update de un item en la base de datos principal de la aplicación
   * @param item
   */
  public async updateItem(item): Promise<void> {
    this.debugService.debug(DebugLevel.TRACE, 'FirebaseService[ updateItem ] : ', 'init');
    try {
      await this.pageContentReference.doc(item.id).update(item);
      this.debugService.debug(DebugLevel.TRACE, 'FirebaseService[ addItem ] /', 'end');
    } catch (error) {
      this.debugService.debug(DebugLevel.DEBUG, 'Error updating firebase item. ', error);
      this.debugService.debug(DebugLevel.ERROR, 'Error updating firebase item. ', null);
    }
  }
  /**
   * Crea un nuevo usuario firebase a partir de su email y password
   * @param email
   * @param password
   */
  public createNewUser(email: string, password: string): Observable<firebase.auth.UserCredential> {
    return from(this.fireAuth.createUserWithEmailAndPassword(email, password));
  }
  /** Get current loged user token */
  public async getFirebaseToken(): Promise<string> {
    const user = await this.fireAuth.currentUser;
    return user.getIdToken();
  }
  /** Get current loged user token */
  public async getFirebaseUser(): Promise<firebase.User> {
    return await this.fireAuth.currentUser;
  }
  /** Do the sign in call to firebase */
  public signinUser(email: string, password: string): Observable<firebase.auth.UserCredential> {
    return from(this.fireAuth.signInWithEmailAndPassword(email, password));
  }
  /** Do the sign out call to firebase */
  public logoutUser(): Observable<void> {
    return from(this.fireAuth.signOut());
  }

  public getUserAsObservable(): Observable<firebase.User> {
    return this.fireAuth.authState.pipe(
      switchMap((user: firebase.User) => {
        this.debugService.debug(DebugLevel.INFO, 'Firebase user change', user);
        return of(user);
      }
      ));
  }

  public doGoogleLogin(): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      const provider = new firebase.auth.GoogleAuthProvider();
      provider.addScope('profile');
      provider.addScope('email');
      this.fireAuth.signInWithPopup(provider)
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          this.debugService.debug(DebugLevel.ERROR, 'Error al autenticar con google mediante popup', err);
          this.debugService.debug(DebugLevel.ERROR, 'Error al autenticar con google mediante popup', null);
        });
    });
  }
}
